import { useCallback, useRef, useState } from "react";

// Tracks an element's rendered width via ResizeObserver, so layout can react
// to container-width changes (e.g. sidebar collapse/expand) rather than only
// viewport-width breakpoints, which miss those resizes entirely.
//
// Uses a callback ref (not a plain useRef) so the observer attaches even when
// the measured element mounts after the hook's first render (e.g. behind a
// loading-state early return) — a plain ref's identity never changes, so an
// effect keyed on it would never re-run once the element appears later. The
// ref is called with null on unmount, which is what disconnects; an unmount
// effect would also run in StrictMode's rehearsal and leave nothing observing.
export const useElementWidth = <T extends HTMLElement>() => {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((element: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!element) return;

    setWidth(element.getBoundingClientRect().width);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    observerRef.current = observer;
  }, []);

  return { ref, width };
};
